import React from 'react';
import { Box, Container, Typography, Button, Stack, Divider } from '@mui/material';
import Header from '../Header';
import SEO from '../SEO';
import Footer from '../Footer';
import { useNavigate } from 'react-router-dom';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.8rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8, mb: 2 }}>
      {children}
    </Typography>
  );
}

export default function BlogCamerasPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="Why Cameras Alone Aren't Enough to Stop Daycare Abuse" description="Three real cases where security cameras existed but failed to prevent abuse. Why behavioral AI — not recording — is the only intervention that works." path="/blog/cameras-not-enough" type="article" />
      <Header onBookDemo={() => navigate('/contact')} />
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 }, px: 3 }}>

        <Box sx={{ display: 'flex', gap: 1, mb: 4, alignItems: 'center' }}>
          <Typography onClick={() => navigate('/blog')} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', cursor: 'pointer', '&:hover': { color: '#5ce1e6' } }}>Blog</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(148,153,176,0.4)' }}>›</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0' }}>Safety Technology</Typography>
        </Box>

        <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 1, display: 'inline-block', mb: 2.5 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Safety Technology · 5 min read</Typography>
        </Box>
        <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 3, lineHeight: 1.1 }}>
          Why Security Cameras Aren't Enough to Protect Your Daycare (And What Is)
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#9499b0', mb: 5, lineHeight: 1.65 }}>
          Bright Horizons had cameras. Three workers abused nine children for 18 months. Here's why cameras record abuse — and why AI stops it.
        </Typography>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        <Section title="The Bright Horizons paradox">
          <Para>In September 2023, three daycare workers at a Manhattan Bright Horizons branch began a pattern of abuse against at least nine children aged 1–2. They placed tape over toddlers' mouths, dragged children by their hair, force-fed ginger shots, hit them with metal bottles, and sprayed bleach in their faces.</Para>
          <Para>The facility had cameras. The abuse lasted until February 2025 — 18 months. The cameras didn't stop it. The cameras didn't even catch it in real time. The abuse was discovered through a tip, after which investigators reviewed footage that had been sitting unanalyzed.</Para>
          <Para>This is the fundamental problem with camera-only systems: they record. They don't watch. They don't flag. They don't alert. They sit there capturing everything and doing nothing with it — until someone decides to look.</Para>
        </Section>

        <Section title="The math of human monitoring">
          <Para>Consider what it would take for a human to actually monitor cameras effectively at a medium-sized daycare:</Para>
          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {[
              'A 10-room facility generates 10 live video feeds simultaneously',
              'A human monitor can effectively watch 2–3 feeds at once before attention degrades',
              'Maintaining focus over an 8-hour shift is physiologically impossible — attention lapses within 20–35 minutes of continuous monitoring (research on security operators)',
              'A single monitor covering all rooms costs $35,000–$55,000/year in salary — and still misses things',
              'At $14/hr average childcare worker pay, this cost is prohibitive for most independent operators',
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#5ce1e6', mt: 0.7, flexShrink: 0 }} />
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
              </Box>
            ))}
          </Stack>
          <Para>The math doesn't work. Human monitoring at scale is expensive, unreliable, and — as Bright Horizons showed — completely misses patterns that build across sessions over time.</Para>
        </Section>

        <Section title="What AI sees that humans miss">
          <Para>SMCLA's behavioral AI was trained specifically on childcare worker behavior. It's not looking for movement or noise. It's looking for behavioral patterns — the kind that only become visible when you're watching every session across weeks and months.</Para>
          <Stack spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Unusual physical contact patterns', detail: 'The same worker repeatedly making aggressive physical contact with the same child — invisible in isolation, obvious at scale.' },
              { label: 'Unauthorized isolation', detail: 'A worker moving a child away from cameras or other staff — a behavioral flag that no camera recording alone would surface.' },
              { label: 'Velocity and force anomalies', detail: 'Physical handling that falls outside normal ranges for the childcare context — moving a child faster, harder, or in ways that diverge from the norm.' },
              { label: 'Time-based pattern detection', detail: 'Behavioral changes during specific shift times, specific days, or with specific staff configurations — patterns that take weeks to emerge.' },
            ].map(({ label, detail }) => (
              <Box key={label} sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 2 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#5ce1e6', mb: 0.75 }}>{label}</Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{detail}</Typography>
              </Box>
            ))}
          </Stack>
        </Section>

        <Section title="The Blanding, Utah case: the camera got it right — too late">
          <Para>In October 2025, a worker at a Blanding, Utah daycare was physically aggressive with a 2-year-old. Security footage captured it. The worker was arrested in January 2026.</Para>
          <Para>The cameras worked exactly as designed. They recorded. The arrest happened two months after the incident, after someone reviewed the footage.</Para>
          <Para>SMCLA would have sent an alert within 60 seconds of the incident. Not two months later. Sixty seconds. At that point, a manager can intervene, the worker can be removed from the floor, and the child's harm can potentially be prevented — not just documented.</Para>
          <Box sx={{ p: 3, bgcolor: 'rgba(92,225,230,0.04)', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 2, mb: 3, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#5ce1e6' }}>
              "The cameras caught it. We would have stopped it."
            </Typography>
          </Box>
        </Section>

        <Section title="SMCLA vs. traditional cameras: the key differences">
          <Stack spacing={2} sx={{ mb: 3 }}>
            {[
              { cameras: 'Records everything', smcla: 'Analyzes everything' },
              { cameras: 'Requires human review to find incidents', smcla: 'Flags incidents automatically, in real time' },
              { cameras: 'Evidence retrieved after the fact', smcla: 'Alert sent within 60 seconds of detection' },
              { cameras: 'Single-incident view', smcla: 'Pattern detection across days, weeks, months' },
              { cameras: 'General footage, not childcare-specific', smcla: 'AI trained specifically on childcare worker behavior' },
              { cameras: 'No staff protection', smcla: 'Exonerates innocent staff from false accusations' },
            ].map(({ cameras, smcla }) => (
              <Box key={cameras} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box sx={{ p: 2, bgcolor: 'rgba(148,153,176,0.05)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.5 }}>📷 {cameras}</Typography>
                </Box>
                <Box sx={{ p: 2, bgcolor: 'rgba(92,225,230,0.04)', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 2 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#5ce1e6', lineHeight: 1.5 }}>🤖 {smcla}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Section>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        <Box sx={{ p: 4, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 3, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 20, color: '#f0f0f8', mb: 1 }}>
            See the difference AI makes
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', mb: 3, lineHeight: 1.65 }}>
            Book a 30-minute demo and see SMCLA's behavioral AI in action — applied to your facility's specific layout and staff structure.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/contact')} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 15, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, '&:hover': { bgcolor: '#4ecfd4' } }}>
            Book a Demo →
          </Button>
        </Box>
      </Container>
      <Footer />
    </Box>
  );
}
